import React, { useState, useEffect, useCallback } from "react";
import { AssessmentTemplate, Section, Question, Trait, ExamResult, TraitScore, SectionScore } from "../../types";
import { useApp } from "../../context/AppContext";
import { db } from "../../firebase/config";
import { doc, updateDoc, collection, getDocs, query, where, getDoc, addDoc, writeBatch } from "firebase/firestore";
import { uid } from "../../utils/helpers";
import { useExamSession } from "../../hooks/useExamSession";
import GlassCard from "../../components/ui/GlassCard";
import Button from "../../components/ui/Button";
import ProgressBar from "../../components/ui/ProgressBar";
import Badge from "../../components/ui/Badge";

interface Props {
  assessmentTemplate: AssessmentTemplate;
  assignment: any;
  candidateId: string;
  onFinish: () => void;
  preloadedSections?: Section[];
  preloadedQuestions?: Question[];
  preloadedTraits?: Trait[];
}

const ExamTakingPage: React.FC<Props> = ({ 
  assessmentTemplate, 
  assignment, 
  candidateId, 
  onFinish,
  preloadedSections = [],
  preloadedQuestions = [],
  preloadedTraits = []
}) => {
  const { traits: contextTraits, setResults, setAssignments } = useApp();
  const [sections, setSections] = useState<Section[]>(preloadedSections);
  const [questions, setQuestions] = useState<Question[]>(preloadedQuestions);
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<ExamResult | null>(null);
  const [showReview, setShowReview] = useState(false);
  
  const traits = preloadedTraits.length > 0 ? preloadedTraits : contextTraits;
  
  const {
    loading: sessionLoading,
    currentSectionIndex,
    currentQuestionIndex,
    answers,
    timeLeft,
    examStarted,
    examSubmitted,
    startExam,
    saveAnswer,
    submitExam,
    goToNextQuestion,
    goToPreviousQuestion,
    jumpToQuestion,
    getAnsweredCount,
    getSectionProgress,
    updateSectionStatus
  } = useExamSession({
    assignmentId: assignment.id,
    candidateId,
    sections,
    questions,
    totalDuration: assessmentTemplate.timeLimit
  });

  useEffect(() => {
    if (preloadedSections.length > 0 && preloadedQuestions.length > 0) {
      setLoading(false);
    }
  }, [preloadedSections, preloadedQuestions]);

  const calculateAndSaveResults = useCallback(async () => {
    let totalScore = 0;
    let maxScore = 0;
    const traitScoresMap = new Map<string, { score: number; maxScore: number; name: string }>();
    const sectionScoresMap = new Map<string, { score: number; maxScore: number; name: string }>();

    questions.forEach(question => {
      const answer = answers[question.id];
      const weightage = question.weightage || 1;
      const maxScale = question.maxScale || 5;
      
      if (answer !== undefined) {
        if (question.type === "likert") {
          const score = question.isReverse ? (maxScale + 1 - answer) : answer;
          totalScore += score * weightage;
          maxScore += maxScale * weightage;
          
          if (question.traitId) {
            const existing = traitScoresMap.get(question.traitId) || { score: 0, maxScore: 0, name: question.traitName || "" };
            existing.score += score * weightage;
            existing.maxScore += maxScale * weightage;
            traitScoresMap.set(question.traitId, existing);
          }
          
          if (question.sectionId) {
            const section = sections.find(s => s.id === question.sectionId);
            const existing = sectionScoresMap.get(question.sectionId) || { score: 0, maxScore: 0, name: section?.name || "Unknown" };
            existing.score += score * weightage;
            existing.maxScore += maxScale * weightage;
            sectionScoresMap.set(question.sectionId, existing);
          }
        } else if (question.type === "mcq") {
          const selectedOption = question.options?.find(opt => opt.value === answer);
          if (selectedOption?.isCorrect) {
            const score = weightage;
            totalScore += score;
            maxScore += weightage;
            
            if (question.traitId) {
              const existing = traitScoresMap.get(question.traitId) || { score: 0, maxScore: 0, name: question.traitName || "" };
              existing.score += score;
              existing.maxScore += weightage;
              traitScoresMap.set(question.traitId, existing);
            }
          } else {
            maxScore += weightage;
            if (question.traitId) {
              const existing = traitScoresMap.get(question.traitId) || { score: 0, maxScore: 0, name: question.traitName || "" };
              existing.maxScore += weightage;
              traitScoresMap.set(question.traitId, existing);
            }
          }
        } else if (question.type === "descriptive") {
          maxScore += weightage;
          if (question.traitId) {
            const existing = traitScoresMap.get(question.traitId) || { score: 0, maxScore: 0, name: question.traitName || "" };
            existing.maxScore += weightage;
            traitScoresMap.set(question.traitId, existing);
          }
        }
      } else {
        if (question.type === "likert") {
          maxScore += maxScale * weightage;
        } else {
          maxScore += weightage;
        }
        
        if (question.traitId) {
          const existing = traitScoresMap.get(question.traitId) || { score: 0, maxScore: 0, name: question.traitName || "" };
          existing.maxScore += question.type === "likert" ? maxScale * weightage : weightage;
          traitScoresMap.set(question.traitId, existing);
        }
        
        if (question.sectionId) {
          const section = sections.find(s => s.id === question.sectionId);
          const existing = sectionScoresMap.get(question.sectionId) || { score: 0, maxScore: 0, name: section?.name || "Unknown" };
          existing.maxScore += question.type === "likert" ? maxScale * weightage : weightage;
          sectionScoresMap.set(question.sectionId, existing);
        }
      }
    });

    const traitScores: TraitScore[] = Array.from(traitScoresMap.entries()).map(([traitId, data]) => {
      const percentage = data.maxScore > 0 ? Math.round((data.score / data.maxScore) * 100) : 0;
      let level: "Low" | "Moderate" | "High" = "Low";
      if (percentage >= 70) level = "High";
      else if (percentage >= 50) level = "Moderate";
      return {
        traitId,
        traitName: data.name,
        score: data.score,
        maxScore: data.maxScore,
        percentage,
        level
      };
    });

    const sectionScores: SectionScore[] = Array.from(sectionScoresMap.entries()).map(([sectionId, data]) => {
      const percentage = data.maxScore > 0 ? Math.round((data.score / data.maxScore) * 100) : 0;
      return {
        sectionId,
        sectionName: data.name,
        score: data.score,
        maxScore: data.maxScore,
        percentage
      };
    });

    const percentage = maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0;
    
    const newResult: ExamResult = {
      id: uid(),
      assignmentId: assignment.id,
      candidateId,
      examId: assessmentTemplate.id,
      orgId: assignment.orgId,
      totalScore,
      maxScore,
      percentage,
      traitScores,
      sectionScores,
      timeSpent: (assessmentTemplate.timeLimit * 60) - timeLeft,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      completedAt: new Date().toISOString()
    };
    
    setResult(newResult);
    setResults(prev => [...prev, newResult]);
    
    const batch = writeBatch(db);
    
    const assignmentRef = doc(db, "exam_assignments", assignment.id);
    batch.update(assignmentRef, {
      status: "completed",
      completedAt: new Date().toISOString(),
      score: totalScore,
      maxScore: maxScore,
      updatedAt: new Date().toISOString()
    });
    
    const resultRef = doc(db, "exam_results", newResult.id);
    batch.set(resultRef, newResult);
    
    await batch.commit();
    
    setAssignments(prev => prev.map(a =>
      a.id === assignment.id
        ? { ...a, status: "completed", completedAt: new Date().toISOString(), score: totalScore, maxScore: maxScore }
        : a
    ));
  }, [answers, questions, sections, assignment.id, assignment.orgId, candidateId, assessmentTemplate.id, assessmentTemplate.timeLimit, timeLeft, setResults, setAssignments]);

  const handleSubmitExam = async () => {
    await calculateAndSaveResults();
    await submitExam();
  };

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  if (loading || sessionLoading) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <GlassCard className="p-12 text-center">
          <div className="animate-spin text-4xl mb-3">⏳</div>
          <p className="text-gray-500">Loading your exam...</p>
        </GlassCard>
      </div>
    );
  }

  if (!examStarted && !examSubmitted) {
    const totalQuestions = questions.length;
    const sectionsCount = sections.length;
    
    return (
      <div className="max-w-2xl mx-auto p-8">
        <GlassCard className="p-8 text-center">
          <div className="text-6xl mb-4">📋</div>
          <h2 className="text-2xl font-extrabold text-gray-800">{assessmentTemplate.name}</h2>
          <p className="text-gray-500 mt-2">{assessmentTemplate.description}</p>
          
          <div className="mt-6 p-4 bg-gray-50 rounded-xl text-left">
            <h3 className="font-bold text-gray-700 mb-3">Exam Instructions:</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• Total Duration: {assessmentTemplate.timeLimit} minutes</li>
              <li>• Total Questions: {totalQuestions}</li>
              <li>• Sections: {sectionsCount}</li>
              <li>• All questions are mandatory</li>
              <li>• You can navigate between sections</li>
              <li>• Your progress will be auto-saved</li>
            </ul>
          </div>
          
          <div className="mt-6 p-4 bg-yellow-50 rounded-xl">
            <p className="text-sm text-yellow-800">
              ⚠️ Please ensure you have a stable internet connection. Do not refresh the page during the exam.
            </p>
          </div>
          
          <div className="flex gap-3 justify-center mt-6">
            <Button variant="secondary" onClick={onFinish}>Cancel</Button>
            <Button onClick={startExam} className="bg-gradient-to-r from-indigo-600 to-purple-600">
              Start Exam →
            </Button>
          </div>
        </GlassCard>
      </div>
    );
  }

  if (examSubmitted && result) {
    const col = result.percentage >= 70 ? "#10b981" : result.percentage >= 50 ? "#f59e0b" : "#ef4444";
    return (
      <div className="max-w-2xl mx-auto space-y-5">
        <GlassCard className="p-8 text-center">
          <div className="text-7xl mb-4">🎉</div>
          <h2 className="text-2xl font-extrabold text-gray-800">Exam Submitted Successfully!</h2>
          <p className="text-gray-500 mt-1">{assessmentTemplate.name}</p>
          <p className="text-6xl font-extrabold mt-5" style={{ color: col }}>{result.percentage}%</p>
          <p className="text-gray-400 text-sm mt-1">{result.totalScore} / {result.maxScore} points</p>
          <div className="mt-4 max-w-xs mx-auto">
            <ProgressBar value={result.percentage} max={100} color={col} height={12} />
          </div>
        </GlassCard>

        {result.traitScores && result.traitScores.length > 0 && (
          <GlassCard className="p-6">
            <h3 className="font-extrabold text-gray-800 mb-4">🧠 Trait Breakdown</h3>
            <div className="space-y-3">
              {result.traitScores.map(ts => {
                const trait = traits.find(t => t.id === ts.traitId);
                let levelColor = "#6366f1";
                if (ts.level === "High") levelColor = "#10b981";
                else if (ts.level === "Moderate") levelColor = "#f59e0b";
                else levelColor = "#ef4444";
                return (
                  <div key={ts.traitId}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span>{trait?.name || "📊"}</span>
                        <span className="text-sm font-bold text-gray-700">{ts.traitName}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-extrabold text-gray-800">{ts.percentage}%</span>
                        <Badge text={ts.level} />
                      </div>
                    </div>
                    <ProgressBar value={ts.score} max={ts.maxScore} color={levelColor} height={9} />
                  </div>
                );
              })}
            </div>
          </GlassCard>
        )}

        {result.sectionScores && result.sectionScores.length > 0 && (
          <GlassCard className="p-6">
            <h3 className="font-extrabold text-gray-800 mb-4">📐 Section Scores</h3>
            <div className="space-y-3">
              {result.sectionScores.map(ss => {
                const c = ss.percentage >= 70 ? "#10b981" : ss.percentage >= 50 ? "#f59e0b" : "#ef4444";
                return (
                  <div key={ss.sectionId}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-bold text-gray-700">{ss.sectionName}</span>
                      <span className="text-sm font-extrabold text-gray-800">{ss.percentage}%</span>
                    </div>
                    <ProgressBar value={ss.score} max={ss.maxScore} color={c} height={9} />
                    <p className="text-xs text-gray-400 mt-0.5">{ss.score}/{ss.maxScore} pts</p>
                  </div>
                );
              })}
            </div>
          </GlassCard>
        )}

        <Button fullWidth onClick={onFinish}>← Back to Dashboard</Button>
      </div>
    );
  }

  if (sections.length === 0) {
    return (
      <GlassCard className="p-12 text-center max-w-md mx-auto">
        <div className="text-5xl mb-4">📭</div>
        <h3 className="font-bold text-gray-700">No sections found for this exam.</h3>
        <Button className="mt-4" onClick={onFinish}>← Go Back</Button>
      </GlassCard>
    );
  }

  if (questions.length === 0) {
    return (
      <GlassCard className="p-12 text-center max-w-md mx-auto">
        <div className="text-5xl mb-4">❓</div>
        <h3 className="font-bold text-gray-700">No questions available for this exam.</h3>
        <Button className="mt-4" onClick={onFinish}>← Go Back</Button>
      </GlassCard>
    );
  }

  const currentSection = sections[currentSectionIndex];
  const sectionQuestions = questions.filter(q => q.sectionId === currentSection?.id);
  const currentQuestion = sectionQuestions[currentQuestionIndex];
  const answeredCount = getAnsweredCount();
  const totalQuestions = questions.length;
  const isLastQuestion = currentSectionIndex === sections.length - 1 && currentQuestionIndex === sectionQuestions.length - 1;
  const trait = traits.find(t => t.id === currentQuestion?.traitId);
  const timePercentage = (timeLeft / (assessmentTemplate.timeLimit * 60)) * 100;
  const isTimeLow = timeLeft < 300;

  const handleAnswer = (value: any) => {
    if (currentQuestion) {
      saveAnswer(currentQuestion.id, value, currentSection.id);
    }
  };

  if (showReview) {
    return (
      <div className="max-w-4xl mx-auto space-y-4">
        <GlassCard className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-extrabold text-gray-800">Review Your Answers</h2>
            <Button variant="secondary" onClick={() => setShowReview(false)}>Continue Exam</Button>
          </div>
          
          {sections.map((section, sectionIdx) => {
            const sectionQuestionsList = questions.filter(q => q.sectionId === section.id);
            const sectionProgress = getSectionProgress(section.id);
            
            return (
              <div key={section.id} className="mb-6">
                <h3 className="font-bold text-gray-700 mb-3">{section.name}</h3>
                <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
                  {sectionQuestionsList.map((q, qIdx) => {
                    const isAnswered = answers[q.id] !== undefined;
                    return (
                      <button
                        key={q.id}
                        onClick={() => {
                          jumpToQuestion(sectionIdx, qIdx);
                          setShowReview(false);
                        }}
                        className={`p-3 rounded-lg text-center font-semibold text-sm transition-all
                          ${isAnswered 
                            ? "bg-green-100 text-green-700 border-2 border-green-300" 
                            : "bg-red-100 text-red-700 border-2 border-red-300"
                          }`}
                      >
                        {qIdx + 1}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {sectionProgress.answered}/{sectionProgress.total} answered ({Math.round(sectionProgress.percentage)}%)
                </p>
              </div>
            );
          })}
          
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600">
                Total: {answeredCount}/{totalQuestions} answered ({Math.round((answeredCount/totalQuestions)*100)}%)
              </p>
              <Button onClick={handleSubmitExam} variant="success">
                Submit Final Exam ✓
              </Button>
            </div>
          </div>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      {/* Header with Timer and Progress */}
      <GlassCard className="px-5 py-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <span className="text-sm font-bold text-gray-700">
              Section {currentSectionIndex + 1}/{sections.length}: {currentSection.name}
            </span>
            <div className="w-40 hidden sm:block">
              <ProgressBar value={currentQuestionIndex + 1} max={sectionQuestions.length} color="#6366f1" height={5} />
            </div>
          </div>
          <div className={`flex items-center gap-2 font-mono text-sm font-extrabold px-3 py-1.5 rounded-xl transition-colors ${isTimeLow ? "bg-red-50 text-red-500 animate-pulse" : "bg-gray-50 text-gray-700"}`}>
            <span>⏱</span>
            <span>{formatTime(timeLeft)}</span>
          </div>
        </div>
        
        <div className="mt-3">
          <ProgressBar value={timePercentage} max={100} color={isTimeLow ? "#ef4444" : "#6366f1"} height={4} />
        </div>
        
        <div className="mt-3 flex justify-between text-xs text-gray-500">
          <span>Overall Progress: {answeredCount}/{totalQuestions}</span>
          <button onClick={() => setShowReview(true)} className="text-indigo-600 hover:underline">
            Review All Answers →
          </button>
        </div>
      </GlassCard>

      {/* Question Card */}
      <GlassCard className="p-6">
        {trait && (
          <div className="mb-3 flex items-center gap-2 flex-wrap">
            <span className="text-xs px-3 py-1 rounded-full font-bold"
              style={{ background: `${ "#6366f1"}18`, color:"#6366f1" }}>
              {trait.name}
            </span>
            {currentQuestion?.isReverse && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 font-semibold">↩ Reverse Coded</span>
            )}
          </div>
        )}
        
        {(currentQuestion?.weightage ?? 0) > 1 && (
          <span className="inline-block mb-3 text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 font-bold">
            Weightage: {currentQuestion?.weightage}×
          </span>
        )}

        <h3 className="text-lg font-bold text-gray-800 leading-relaxed mb-6">
          Q{currentQuestionIndex + 1}: {currentQuestion?.text}
        </h3>

        <div className="space-y-3">
          {currentQuestion?.type === "likert" && (
            <div className="grid grid-cols-5 gap-2">
              {[1, 2, 3, 4, 5].map(value => {
                const labels: Record<number, string> = {
                  1: "Strongly Disagree",
                  2: "Disagree",
                  3: "Neutral",
                  4: "Agree",
                  5: "Strongly Agree"
                };
                return (
                  <button
                    key={value}
                    onClick={() => handleAnswer(value)}
                    className={`p-3 rounded-xl text-center transition-all duration-150
                      ${answers[currentQuestion.id] === value
                        ? "bg-indigo-500 text-white shadow-md scale-105"
                        : "bg-gray-100 text-gray-700 hover:bg-indigo-100"
                      }`}
                  >
                    <div className="font-bold text-lg">{value}</div>
                    <div className="text-xs mt-1 hidden md:block">{labels[value]}</div>
                  </button>
                );
              })}
            </div>
          )}
          
          {currentQuestion?.type === "mcq" && currentQuestion.options?.map(opt => {
            const isSelected = answers[currentQuestion.id] === opt.value;
            return (
              <button
                key={opt.id}
                onClick={() => handleAnswer(opt.value)}
                className={`w-full text-left px-5 py-3.5 rounded-xl border-2 text-sm font-semibold transition-all duration-150
                  ${isSelected
                    ? "border-indigo-500 bg-indigo-50 text-indigo-800 shadow-md"
                    : "border-gray-200/60 bg-white/40 text-gray-700 hover:border-indigo-300 hover:bg-indigo-50/40"
                  }`}
              >
                <span className={`inline-block w-6 h-6 rounded-full border-2 mr-3 align-middle transition-colors flex-shrink-0
                  ${isSelected ? "border-indigo-500 bg-indigo-500" : "border-gray-300 bg-white"}`}
                  style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                  {isSelected && <span className="w-2 h-2 rounded-full bg-white" />}
                </span>
                {opt.label}
              </button>
            );
          })}
          
          {currentQuestion?.type === "descriptive" && (
            <textarea
              className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:outline-none"
              rows={6}
              value={answers[currentQuestion.id] || ""}
              onChange={(e) => handleAnswer(e.target.value)}
              placeholder="Type your detailed answer here..."
            />
          )}
        </div>
      </GlassCard>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between gap-3">
        <Button 
          variant="secondary" 
          onClick={goToPreviousQuestion} 
          disabled={currentSectionIndex === 0 && currentQuestionIndex === 0}
        >
          ← Previous
        </Button>

        <div className="flex gap-2">
          <span className="text-xs text-gray-400">
            {answeredCount}/{totalQuestions} answered
          </span>
        </div>

        {!isLastQuestion ? (
          <Button onClick={goToNextQuestion}>Next →</Button>
        ) : (
          <Button variant="success" onClick={handleSubmitExam}>Submit Exam ✓</Button>
        )}
      </div>

      {/* Section Navigator */}
      <GlassCard className="p-4">
        <p className="text-xs font-bold text-gray-500 mb-3 uppercase tracking-wider">Section Navigator</p>
        <div className="flex flex-wrap gap-2">
          {sections.map((section, idx) => {
            const sectionProgress = getSectionProgress(section.id);
            const isActive = idx === currentSectionIndex;
            let statusColor = "bg-gray-100 text-gray-700";
            if (sectionProgress.percentage === 100) statusColor = "bg-green-100 text-green-700";
            else if (sectionProgress.percentage > 0) statusColor = "bg-yellow-100 text-yellow-700";
            
            return (
              <button
                key={section.id}
                onClick={() => jumpToQuestion(idx, 0)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${statusColor} ${isActive ? "ring-2 ring-indigo-500" : ""}`}
              >
                {section.name}
                <span className="ml-2 text-xs">
                  ({sectionProgress.answered}/{sectionProgress.total})
                </span>
              </button>
            );
          })}
        </div>
      </GlassCard>
    </div>
  );
};

// Export helper functions
export async function saveExamResultsBatch(results: ExamResult[], assignmentId: string) {
  try {
    const batch = writeBatch(db);
    
    for (const result of results) {
      const resultRef = doc(db, "exam_results", result.id);
      batch.set(resultRef, result);
    }
    
    const assignmentRef = doc(db, "exam_assignments", assignmentId);
    batch.update(assignmentRef, {
      status: "completed",
      completedAt: new Date().toISOString()
    });
    
    await batch.commit();
    return true;
  } catch (error) {
    console.error("Error saving exam results:", error);
    throw error;
  }
}

export async function getCandidateResults(candidateId: string, examId?: string) {
  try {
    const resultsRef = collection(db, "exam_results");
    let q = query(resultsRef, where("candidateId", "==", candidateId));
    
    if (examId) {
      q = query(resultsRef, where("candidateId", "==", candidateId), where("examId", "==", examId));
    }
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as ExamResult));
  } catch (error) {
    console.error("Error getting candidate results:", error);
    return [];
  }
}

export default ExamTakingPage;